import * as XLSX from 'xlsx';
import { logInfo, logWarn } from '../config_and_helpers/logger';

const LOG_CONTEXT = 'inventoryProductListExcelValidator';

/** Maximum non-empty CAT# data rows allowed (matches backend). */
export const INVENTORY_LIST_MAX_ROWS = 500;

/**
 * Column header aliases aligned with backend inventory_product_list_import.
 * First exact/partial match wins per field.
 */
export const INVENTORY_LIST_COLUMN_ALIASES = {
  cat_num: [
    'cat_num',
    'cat num',
    'cat#',
    'cat #',
    'cat',
    'catalog number',
    'catalogue number',
    'catalog no',
    'catalog',
    'מק"ט',
    'מקט',
    'מספר קטלוג',
    'sku',
    'item number',
    'part number',
  ],
  name: [
    'name',
    'product',
    'product name',
    'item',
    'product description',
    'description',
    'שם',
    'שם מוצר',
    'שם פריט',
    'תיאור',
  ],
  quantity: ['quantity', 'qty', 'amount', 'count', 'כמות', 'כמות נדרשת'],
};

const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Normalize header cell for matching.
 * @param {*} cell
 * @returns {string}
 */
export function normalizeHeader(cell) {
  if (cell == null) return '';
  return String(cell).trim().toLowerCase();
}

/**
 * Find column indices for cat_num, name, quantity on normalized headers.
 * @param {string[]} headers
 * @returns {{ cat_num: number, name?: number, quantity?: number }}
 */
export function findInventoryListColumnIndices(headers) {
  const indices = {};
  for (const [field, aliases] of Object.entries(INVENTORY_LIST_COLUMN_ALIASES)) {
    const idx = headers.findIndex(h => {
      const normalized = (h || '').trim();
      if (normalized.length < 1) return false;
      return aliases.some(alias => {
        const a = (alias || '').trim().toLowerCase();
        if (normalized === a) return true;
        if (normalized.length < 2) return false;
        return normalized.includes(a) || a.includes(normalized);
      });
    });
    if (idx !== -1) indices[field] = idx;
  }
  return indices;
}

/**
 * @param {string} name
 * @returns {boolean}
 */
export function isXlsxFile(name, mimeType) {
  const lower = (name || '').toLowerCase();
  if (lower.endsWith('.xlsx')) return true;
  return mimeType === XLSX_MIME;
}

/**
 * Parse quantity cell; returns null if empty (backend defaults to 1).
 * @param {*} cell
 * @returns {number|null}
 */
function parseQuantityCell(cell) {
  if (cell == null || String(cell).trim() === '') return null;
  const n = Number(String(cell).trim());
  if (!Number.isFinite(n) || !Number.isInteger(n)) return NaN;
  return n;
}

/**
 * Read file bytes (FileReader fallback for Jest / older environments).
 * @param {File|Blob} file
 * @returns {Promise<ArrayBuffer>}
 */
function readFileAsArrayBuffer(file) {
  if (file && typeof file.arrayBuffer === 'function') {
    return file.arrayBuffer();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('File read failed'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Resolve the active worksheet name (matches openpyxl `workbook.active` on the backend).
 * SheetJS exposes the active tab index via `Workbook.WBView[0].activeTab` when present.
 *
 * @param {import('xlsx').WorkBook} workbook
 * @returns {string}
 */
export function resolveActiveSheetName(workbook) {
  const sheetNames = workbook.SheetNames || [];
  if (!sheetNames.length) {
    throw new Error('Workbook has no worksheets.');
  }
  const activeTab = workbook.Workbook?.WBView?.[0]?.activeTab;
  if (
    typeof activeTab === 'number' &&
    activeTab >= 0 &&
    activeTab < sheetNames.length
  ) {
    return sheetNames[activeTab];
  }
  // Fallback when active-tab metadata is missing (e.g. some generated files).
  return sheetNames[0];
}

/**
 * Read the workbook active worksheet from an ArrayBuffer.
 * @param {ArrayBuffer} buffer
 * @returns {{ sheet: import('xlsx').WorkSheet, sheetName: string }}
 */
function readActiveSheet(buffer) {
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheetName = resolveActiveSheetName(workbook);
  return { sheet: workbook.Sheets[sheetName], sheetName };
}

/**
 * @param {import('xlsx').WorkSheet} sheet
 * @returns {{ headers: string[], rows: any[][] }}
 */
function sheetToRows(sheet) {
  const data = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    raw: false,
    defval: '',
  });
  if (!data || data.length < 1) {
    return { headers: [], rows: [] };
  }
  const headers = data[0].map(c => normalizeHeader(c));
  const rows = data.slice(1);
  return { headers, rows };
}

/**
 * Build a downloadable template workbook for inventory list import.
 * @returns {Blob}
 */
export function buildInventoryListTemplateWorkbook() {
  const rows = [
    ['CAT#', 'Product name', 'Quantity'],
    ['HS 120538', 'Example reagent A', 4],
    ['ZR-R1057', 'Example reagent B', 1],
  ];
  const sheet = XLSX.utils.aoa_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, 'Products');
  const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  return new Blob([buffer], { type: XLSX_MIME });
}

/**
 * Trigger browser download of the inventory list Excel template.
 */
export function downloadInventoryListTemplate() {
  const blob = buildInventoryListTemplateWorkbook();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'inventory-product-list-template.xlsx';
  anchor.click();
  URL.revokeObjectURL(url);
  logInfo(LOG_CONTEXT, 'template downloaded');
}

/**
 * Validate an inventory product list Excel file before calling preview API.
 *
 * @param {File} file - `.xlsx` file from file input
 * @returns {Promise<{
 *   valid: boolean,
 *   errors: string[],
 *   warnings: string[],
 *   meta: { rowCount: number, detectedColumns: Record<string, string>, sheetName?: string }
 * }>}
 */
export async function validateInventoryProductListFile(file) {
  const errors = [];
  const warnings = [];
  const meta = { rowCount: 0, detectedColumns: {} };

  if (!file) {
    return { valid: false, errors: ['No file selected.'], warnings, meta };
  }

  if (!isXlsxFile(file.name, file.type)) {
    errors.push(
      `${file.name}: Only Excel .xlsx files are supported (not .xls or .csv).`
    );
    logWarn(LOG_CONTEXT, 'invalid file type', { fileName: file.name, type: file.type });
    return { valid: false, errors, warnings, meta };
  }

  let buffer;
  try {
    buffer = await readFileAsArrayBuffer(file);
  } catch (err) {
    logWarn(LOG_CONTEXT, 'read file failed', err);
    errors.push('Could not read the selected file.');
    return { valid: false, errors, warnings, meta };
  }

  let sheet;
  let sheetName;
  try {
    ({ sheet, sheetName } = readActiveSheet(buffer));
    meta.sheetName = sheetName;
  } catch (err) {
    logWarn(LOG_CONTEXT, 'parse workbook failed', err);
    errors.push(
      err?.message || 'Invalid or corrupt Excel workbook. Please use a valid .xlsx file.'
    );
    return { valid: false, errors, warnings, meta };
  }

  const { headers, rows } = sheetToRows(sheet);
  if (headers.length === 0) {
    errors.push('Row 1 must contain column headers.');
    return { valid: false, errors, warnings, meta };
  }

  const col = findInventoryListColumnIndices(headers);
  if (col.cat_num == null) {
    errors.push(
      'Could not find a CAT# column in row 1. Use a header such as "CAT#", "cat_num", "catalog number", or "מקט".'
    );
    return { valid: false, errors, warnings, meta };
  }

  const rawHeaders = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    raw: false,
    defval: '',
  })[0];
  if (rawHeaders) {
    if (col.cat_num != null) meta.detectedColumns.cat_num = String(rawHeaders[col.cat_num] ?? '').trim();
    if (col.name != null) meta.detectedColumns.name = String(rawHeaders[col.name] ?? '').trim();
    if (col.quantity != null) {
      meta.detectedColumns.quantity = String(rawHeaders[col.quantity] ?? '').trim();
    }
  }

  let rowCount = 0;
  rows.forEach((row, index) => {
    const catVal =
      col.cat_num != null ? String(row[col.cat_num] ?? '').trim() : '';
    if (!catVal) return;
    rowCount += 1;
    if (col.quantity != null) {
      const qty = parseQuantityCell(row[col.quantity]);
      if (qty != null && (Number.isNaN(qty) || qty < 1)) {
        warnings.push(
          `Row ${index + 2}: Quantity must be an integer ≥ 1 (will default to 1 on import).`
        );
      }
    }
  });

  meta.rowCount = rowCount;

  if (rowCount === 0) {
    errors.push('No data rows with a CAT# value were found (starting from row 2).');
    return { valid: false, errors, warnings, meta };
  }

  if (rowCount > INVENTORY_LIST_MAX_ROWS) {
    errors.push(
      `Too many rows (${rowCount}). Maximum is ${INVENTORY_LIST_MAX_ROWS} non-empty CAT# rows.`
    );
    return { valid: false, errors, warnings, meta };
  }

  logInfo(LOG_CONTEXT, 'validation passed', {
    fileName: file.name,
    rowCount,
    detectedColumns: meta.detectedColumns,
  });

  return { valid: true, errors, warnings, meta };
}
